import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { notification } from 'antd'
import isNil from 'lodash/isNil'
import { getAdminSettings } from 'enrollment-platform/utils/adminSettings'
import {
  useGetDeliveriesQuery,
  useGetDeliveryStatsQuery,
  useSendDeliveryMutation,
  useBulkSendDeliveriesMutation,
  useDeleteDeliveryMutation,
  useDownloadInvoiceArchiveMutation,
  useSetDeliveryArchivedMutation,
  useBulkSyncSendGridMutation,
} from '../../api/invoice-delivery-api-client'
import {
  BulkUpdateDeliveryStatusResponseDto,
  InvoiceDeliveryItemDto,
  InvoiceDeliveryStatus,
  InvoiceTemplate,
} from '../../api/dto/invoice-delivery.dto'
import { useSelectedBillingMonth } from './useSelectedBillingMonth'
import { useTemplateRows } from './emailTemplates/useTemplateRows'
import { getAllowedInvoiceTemplates } from './emailTemplates/invoiceTemplates'
import {
  canSendDelivery,
  formatBytes,
  hasBeenSent,
  hasInvoiceFile,
} from './utils'

const SEARCH_DEBOUNCE_MS = 350

/*
 * Cadence for polling the deliveries list and stats while at least one row
 * is in flight. Webhooks usually settle within seconds, so 15s keeps the UI
 * responsive without hammering the BFF for healthy sends.
 */
const IN_FLIGHT_POLL_INTERVAL_MS = 15_000

const getRefetchInterval = (sending: number): number | false =>
  sending > 0 ? IN_FLIGHT_POLL_INTERVAL_MS : false

/*
 * The backend only returns warnings an operator can still act on, so a
 * non-empty list is the single signal for "this row needs fixing". Never
 * re-derive that rule here, or the banner count and the table drift apart.
 */
const hasUnresolvedWarnings = (delivery: InvoiceDeliveryItemDto): boolean =>
  (delivery.parseWarnings?.length ?? 0) > 0

const notifyNoWarningsVisible = (): void => {
  notification.info({
    message: 'Nothing to fix in this list',
    description:
      'No visible invoice has an unreadable value. If you expect one, enable "Show archived" to include archived deliveries.',
  })
}

const useInvoiceDelivery = () => {
  // Billing month is shared with Generation mode via the URL `month` param.
  const { selectedMonth, setSelectedMonth, billingMonth, billingYear } =
    useSelectedBillingMonth()
  const [statusFilter, setStatusFilter] = useState<
    InvoiceDeliveryStatus | undefined
  >(undefined)
  const [templateFilter, setTemplateFilter] = useState<
    InvoiceTemplate | undefined
  >(undefined)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  // Session-only dismissal — banner reappears on next page load while count > 0
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [warningBannerDismissed, setWarningBannerDismissed] = useState(false)
  const [resolveModalOpen, setResolveModalOpen] = useState(false)
  // When true, open the first warning delivery as soon as the unfiltered list loads.
  const [pendingOpenFirstWarning, setPendingOpenFirstWarning] = useState(false)
  // When true, drawer prev/next skips over clean invoices and only walks flagged ones.
  const [warningsOnlyNav, setWarningsOnlyNav] = useState(false)
  const [drawerDelivery, setDrawerDelivery] =
    useState<InvoiceDeliveryItemDto | null>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  // Open review modal, or null. `ids` is what the modal previews.
  const [reviewModal, setReviewModal] = useState<{
    scope: 'single' | 'bulk'
    ids: string[]
  } | null>(null)

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search)
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [search])

  const billingPeriodStart = `${billingYear}-${String(billingMonth).padStart(2, '0')}-01`
  const allowedTemplates = getAllowedInvoiceTemplates(billingMonth)
  // A January-only filter carried into another month would empty the list.
  const activeTemplateFilter =
    !isNil(templateFilter) && allowedTemplates.includes(templateFilter)
      ? templateFilter
      : undefined

  const { showTestData } = getAdminSettings().finance
  const excludeTestClients = showTestData ? undefined : true

  /*
   * Archive scope shared by the deliveries list and the stats counts. Both
   * queries must agree: a count taken over a wider set than the table lists
   * produces banner warnings for invoices the operator cannot open or fix.
   */
  const archivedFilter = showArchived ? {} : { isArchived: false }

  const statsQuery = useGetDeliveryStatsQuery(
    { billingPeriodStart, excludeTestClients, ...archivedFilter },
    {
      refetchInterval: (query) =>
        getRefetchInterval(query.state.data?.counts?.sending ?? 0),
    }
  )
  const rawCounts = statsQuery.data?.counts
  const stats = {
    total: rawCounts?.total ?? 0,
    draft: rawCounts?.draft ?? 0,
    sending: rawCounts?.sending ?? 0,
    delivered: rawCounts?.delivered ?? 0,
    failed: rawCounts?.failed ?? 0,
    unmatched: rawCounts?.unmatched ?? 0,
    withWarnings: rawCounts?.withWarnings ?? 0,
  }

  const deliveriesQuery = useGetDeliveriesQuery(
    {
      billingPeriodStart,
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(activeTemplateFilter
        ? { invoiceTemplate: activeTemplateFilter }
        : {}),
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      ...archivedFilter,
      excludeTestClients,
    },
    {
      // Poll while any row in this query is still Sending.
      refetchInterval: (query) =>
        query.state.data?.deliveries.some(
          (d) => d.status === InvoiceDeliveryStatus.Sending
        )
          ? IN_FLIGHT_POLL_INTERVAL_MS
          : false,
    }
  )

  /*
   * The whole month, ignoring the table's filters, for month-wide checks: the
   * templates a company already has, OE1's paired OE2 and the bulk bar's
   * selection summary. Archived invoices are included because they still
   * hold their template; consumers that pair or count live invoices skip them.
   */
  const monthQuery = useGetDeliveriesQuery({
    billingPeriodStart,
    excludeTestClients,
  })
  const monthDeliveries = useMemo(
    () => monthQuery.data?.deliveries ?? [],
    [monthQuery.data]
  )
  const listedDeliveries = useMemo(
    () => deliveriesQuery.data?.deliveries ?? [],
    [deliveriesQuery.data]
  )
  const { orderedDeliveries: deliveries, getRowInfo: getTemplateRowInfo } =
    useTemplateRows(listedDeliveries)
  const totalDeliveries = stats.total

  // After "Resolve issues" clears filters, open the first flagged delivery once
  // the fresh unfiltered list arrives, and enable warnings-only navigation.
  useEffect(() => {
    if (!pendingOpenFirstWarning || deliveriesQuery.isFetching) return
    const target = deliveries.find(hasUnresolvedWarnings)
    if (isNil(target)) {
      notifyNoWarningsVisible()
    } else {
      setWarningsOnlyNav(true)
      setDrawerDelivery(target)
    }
    setPendingOpenFirstWarning(false)
  }, [pendingOpenFirstWarning, deliveriesQuery.isFetching, deliveries])

  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set())

  const sendMutation = useSendDeliveryMutation({
    onSuccess: (data, id) => {
      setSendingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      // The send endpoint awaits the SendGrid dispatch and returns the final
      // status. Surface what actually happened: a revert to Draft (e.g. no
      // eligible recipients, no email config) or a Failed mark would otherwise
      // be invisible to the operator until the next polling tick.
      switch (data.status) {
        case InvoiceDeliveryStatus.Draft:
          notification.warning({
            message: 'Delivery did not send',
            description:
              'No eligible recipients or missing email configuration. Check the billing profile and try again.',
          })
          break
        case InvoiceDeliveryStatus.Failed:
          notification.error({ message: 'Delivery failed to send' })
          break
        default:
          // A resend names the addresses it contacted, so the operator can see
          // it went only to the recipients who had not received the invoice.
          if (data.sendOutcome === 'resent') {
            const count = data.sentTo?.length ?? 0
            notification.success({
              message: `Resending to ${count} recipient${count === 1 ? '' : 's'}`,
              description: data.sentTo?.join(', '),
            })
          } else {
            notification.success({ message: 'Delivery queued for sending' })
          }
      }
    },
    onError: (error, id) => {
      setSendingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      notification.error({
        message: 'Failed to send delivery',
        description: error.message,
      })
    },
  })

  const deleteMutation = useDeleteDeliveryMutation({
    onSuccess: (_, id) => {
      notification.success({ message: 'Delivery deleted' })
      setDrawerDelivery((prev) => (prev?.id === id ? null : prev))
    },
    onError: (error) => {
      notification.error({
        message: 'Failed to delete delivery',
        description: error.message,
      })
    },
  })

  /*
   * Reports the mix a send produced. Shared by bulk send and the review
   * modal's confirm: both return the same per-delivery outcome shape, and an
   * operator should read the same sentence either way.
   */
  const notifySendResult = useCallback(
    (data: BulkUpdateDeliveryStatusResponseDto | undefined) => {
      const parts: string[] = []
      const sent = data?.sentCount ?? 0
      const resent = data?.resentCount ?? 0
      const skipped = data?.skippedCount ?? 0
      const failed = data?.failedCount ?? 0
      if (sent > 0) parts.push(`${sent} sent`)
      if (resent > 0) parts.push(`${resent} resent`)
      if (skipped > 0) parts.push(`${skipped} skipped (nothing selected)`)
      if (failed > 0) parts.push(`${failed} failed`)

      if (parts.length === 0) {
        notification.success({ message: 'Deliveries queued for sending' })
      } else if (failed > 0) {
        notification.warning({
          message: 'Send completed with issues',
          description: parts.join(' · '),
        })
      } else {
        notification.success({
          message: 'Send queued',
          description: parts.join(' · '),
        })
      }
    },
    []
  )

  const bulkSendMutation = useBulkSendDeliveriesMutation({
    onSuccess: (data) => {
      /*
       * Bulk send decides per delivery whether to do a full send or a targeted
       * resend, and skips rows with nothing outstanding. Report the mix rather
       * than a single total, so an operator can tell that (say) 2 of 5 selected
       * invoices were left alone because everyone already had them.
       */
      const parts: string[] = []
      const sent = data?.sentCount ?? 0
      const resent = data?.resentCount ?? 0
      const skipped = data?.skippedCount ?? 0
      const failed = data?.failedCount ?? 0
      if (sent > 0) parts.push(`${sent} sent`)
      if (resent > 0) parts.push(`${resent} resent to undelivered recipients`)
      if (skipped > 0) parts.push(`${skipped} skipped (nothing outstanding)`)
      if (failed > 0) parts.push(`${failed} failed`)

      if (parts.length === 0) {
        const count = data?.deliveries?.length ?? 0
        notification.success({
          message:
            count > 0
              ? `${count} ${count === 1 ? 'delivery' : 'deliveries'} queued for sending`
              : 'Deliveries queued for sending',
        })
      } else if (failed > 0) {
        notification.warning({
          message: 'Bulk send completed with issues',
          description: parts.join(' · '),
        })
      } else {
        notification.success({
          message: 'Bulk send queued',
          description: parts.join(' · '),
        })
      }
      setSelectedRowKeys([])
    },
    onError: (error) => {
      notification.error({
        message: 'Failed to send deliveries',
        description: error.message,
      })
    },
  })

  const setArchivedMutation = useSetDeliveryArchivedMutation({
    onSuccess: (_, variables) => {
      notification.success({
        message: variables.body.isArchived
          ? 'Delivery archived'
          : 'Delivery unarchived',
      })
    },
    onError: (error) => {
      notification.error({
        message: 'Failed to update archived state',
        description: error.message,
      })
    },
  })

  const downloadArchiveMutation = useDownloadInvoiceArchiveMutation({
    onSuccess: ({ fileName }) => {
      notification.success({
        message: 'Download started',
        description: fileName,
      })
    },
    onError: (error) => {
      notification.error({
        message: 'Failed to download invoices',
        description: error.message,
      })
    },
  })
  const isDownloadingArchive = downloadArchiveMutation.isPending
  const downloadedBytesLabel = isDownloadingArchive
    ? formatBytes(downloadArchiveMutation.receivedBytes)
    : null

  const bulkSyncMutation = useBulkSyncSendGridMutation()
  const isBulkSyncing = bulkSyncMutation.isPending

  const isBulkSending = bulkSendMutation.isPending

  const isMutating =
    isBulkSending || deleteMutation.isPending || setArchivedMutation.isPending

  /*
   * Send and download have different eligibility rules, so the two are tracked
   * separately off one set of checkboxes.
   *
   * Sendable: matched, latest, has a billing profile, and in a status that can
   * still be dispatched. Downloadable: anything with a file — an already
   * delivered or archived invoice is exactly what finance reconciles against.
   *
   * Both sets are reconciled against the current dataset so stale keys (e.g.
   * left over after a filter or search change) can't silently submit.
   */
  const sendableIds = useMemo(() => {
    return new Set(deliveries.filter(canSendDelivery).map((d) => d.id))
  }, [deliveries])

  const downloadableIds = useMemo(
    () => new Set(deliveries.filter(hasInvoiceFile).map((d) => d.id)),
    [deliveries]
  )

  // Drives the table's checkbox state: every checked row still present in the
  // dataset, regardless of which action it is eligible for.
  const effectiveSelectedRowKeys = useMemo(
    () => selectedRowKeys.filter((key) => downloadableIds.has(String(key))),
    [selectedRowKeys, downloadableIds]
  )

  const selectedSendableIds = useMemo(
    () =>
      effectiveSelectedRowKeys
        .map(String)
        .filter((key) => sendableIds.has(key)),
    [effectiveSelectedRowKeys, sendableIds]
  )

  /*
   * A draft has never gone out, so nobody can receive a duplicate and it
   * dispatches straight away. Anything already sent opens the review modal
   * instead — resending is the case where an operator needs to see, and
   * choose, exactly who gets contacted.
   */
  const handleSend = useCallback(
    (id: string) => {
      const delivery = deliveries.find((d) => d.id === id)
      if (delivery && hasBeenSent(delivery)) {
        setReviewModal({ scope: 'single', ids: [id] })
        return
      }
      setSendingIds((prev) => new Set(prev).add(id))
      sendMutation.mutate(id)
    },
    [sendMutation, deliveries]
  )

  const handleSelectedRowKeysChange = useCallback((keys: React.Key[]) => {
    setSelectedRowKeys(keys)
  }, [])

  /*
   * Both entry points now go through the review modal rather than dispatching.
   * A first send of a draft is the one exception (see handleSend) — it has no
   * recipients to review and nobody can receive a duplicate.
   */
  const handleOpenReview = useCallback(
    (scope: 'single' | 'bulk', ids: string[]) => {
      if (ids.length === 0) return
      setReviewModal({ scope, ids })
    },
    []
  )

  const handleCloseReview = useCallback(() => {
    setReviewModal(null)
  }, [])

  const handleReviewSent = useCallback(
    (data: BulkUpdateDeliveryStatusResponseDto) => {
      notifySendResult(data)
      setSelectedRowKeys([])
    },
    [notifySendResult]
  )

  const handleBulkSend = useCallback(() => {
    // Sendable subset only — the checkboxes now also cover download-only rows,
    // and previewing a row that can never be sent would just add noise to the
    // review modal.
    handleOpenReview('bulk', selectedSendableIds)
  }, [handleOpenReview, selectedSendableIds])

  const handleDownloadSelected = useCallback(() => {
    const ids = effectiveSelectedRowKeys.map(String)
    if (ids.length === 0) return
    downloadArchiveMutation.mutate({ ids })
  }, [downloadArchiveMutation, effectiveSelectedRowKeys])

  /*
   * Deliberately scoped to the billing period rather than the visible table:
   * this ignores the status filter, search text and archive toggle so the
   * result is reproducible regardless of how the operator left the view.
   */
  const handleDownloadMonth = useCallback(() => {
    downloadArchiveMutation.mutate({ billingPeriodStart, excludeTestClients })
  }, [downloadArchiveMutation, billingPeriodStart, excludeTestClients])

  const handleBulkSync = useCallback(() => {
    bulkSyncMutation.mutate(billingPeriodStart, {
      onSuccess: ({
        syncedCount,
        failedCount,
        skippedCount,
        sgErrorCount,
        stuckAutoFailedCount,
      }) => {
        const parts: string[] = []
        const label = syncedCount === 1 ? 'delivery' : 'deliveries'
        parts.push(`Resynced ${syncedCount} ${label}`)
        if (failedCount > 0) parts.push(`${failedCount} failed`)
        if (sgErrorCount > 0) {
          parts.push(
            `${sgErrorCount} SendGrid error${sgErrorCount === 1 ? '' : 's'}`
          )
        }
        if (stuckAutoFailedCount > 0) {
          parts.push(`${stuckAutoFailedCount} stuck → failed`)
        }
        if (skippedCount > 0) parts.push(`${skippedCount} skipped`)
        const message = parts.join(', ')

        const hasIssue =
          failedCount > 0 || sgErrorCount > 0 || stuckAutoFailedCount > 0
        if (!hasIssue) {
          notification.success({ message })
        } else {
          const descriptions: string[] = []
          if (sgErrorCount > 0) {
            descriptions.push(
              'SendGrid did not respond for some deliveries — try again in a moment.'
            )
          }
          if (stuckAutoFailedCount > 0) {
            descriptions.push(
              `${stuckAutoFailedCount} ${stuckAutoFailedCount === 1 ? 'delivery' : 'deliveries'} stuck SENDING with no SendGrid record past 30 min — marked failed. Investigate and re-upload.`
            )
          }
          notification.warning({
            message,
            description:
              descriptions.length > 0 ? descriptions.join(' ') : undefined,
          })
        }
      },
      onError: (error: Error) => {
        notification.error({
          message: 'Failed to sync SendGrid status',
          description: error.message,
        })
      },
    })
  }, [billingPeriodStart, bulkSyncMutation])

  const handleDelete = useCallback(
    (id: string) => {
      deleteMutation.mutate(id)
    },
    [deleteMutation]
  )

  const handleSetArchived = useCallback(
    (id: string, isArchived: boolean) => {
      setArchivedMutation.mutate({ id, body: { isArchived } })
    },
    [setArchivedMutation]
  )

  const handleUploadComplete = useCallback(() => {
    setBannerDismissed(false)
    setWarningBannerDismissed(false)
    deliveriesQuery.refetch()
    statsQuery.refetch()
  }, [deliveriesQuery, statsQuery])

  const handleStatusFilter = useCallback(
    (status: InvoiceDeliveryStatus | undefined) => {
      setStatusFilter(status)
    },
    []
  )

  const handleDismissBanner = useCallback(() => {
    setBannerDismissed(true)
  }, [])

  const handleDismissWarningBanner = useCallback(() => {
    setWarningBannerDismissed(true)
  }, [])

  const handleToggleArchived = useCallback(() => {
    setShowArchived((v) => !v)
  }, [])

  const handleBillingMonthChange = useCallback(
    (month: number) => {
      setSelectedMonth(selectedMonth.month(month - 1))
    },
    [selectedMonth, setSelectedMonth]
  )

  const handleBillingYearChange = useCallback(
    (year: number) => {
      setSelectedMonth(selectedMonth.year(year))
    },
    [selectedMonth, setSelectedMonth]
  )

  const handleRowClick = useCallback((delivery: InvoiceDeliveryItemDto) => {
    setDrawerDelivery(delivery)
  }, [])

  const handleDrawerClose = useCallback(() => {
    setDrawerDelivery(null)
    setWarningsOnlyNav(false)
  }, [])

  // In warnings-only mode, nav list is restricted to flagged invoices.
  const navDeliveries = useMemo(() => {
    if (!warningsOnlyNav) return deliveries
    return deliveries.filter(hasUnresolvedWarnings)
  }, [deliveries, warningsOnlyNav])

  const currentNavIndex = useMemo(() => {
    if (!drawerDelivery) return -1
    return navDeliveries.findIndex((d) => d.id === drawerDelivery.id)
  }, [navDeliveries, drawerDelivery])

  // Warnings-only mode: when the user fixes the current delivery, it drops out
  // of `navDeliveries`. Auto-advance to the next flagged one so they can keep
  // working through the queue. Close the drawer when none remain.
  useEffect(() => {
    if (
      !warningsOnlyNav ||
      !drawerDelivery ||
      deliveriesQuery.isFetching ||
      currentNavIndex !== -1
    ) {
      return
    }
    if (navDeliveries.length === 0) {
      setDrawerDelivery(null)
      setWarningsOnlyNav(false)
      return
    }
    setDrawerDelivery(navDeliveries[0])
  }, [
    warningsOnlyNav,
    drawerDelivery,
    deliveriesQuery.isFetching,
    currentNavIndex,
    navDeliveries,
  ])

  const canNavigatePrev = currentNavIndex > 0
  const canNavigateNext =
    currentNavIndex >= 0 && currentNavIndex < navDeliveries.length - 1

  const handleDrawerNavigate = useCallback(
    (direction: 'prev' | 'next') => {
      if (currentNavIndex < 0) return
      const target =
        direction === 'prev' ? currentNavIndex - 1 : currentNavIndex + 1
      if (target < 0 || target >= navDeliveries.length) return
      setDrawerDelivery(navDeliveries[target])
    },
    [currentNavIndex, navDeliveries]
  )

  const handleOpenResolveModal = useCallback(() => {
    setResolveModalOpen(true)
  }, [])

  // Clears active filters so all flagged deliveries are visible, then opens the
  // drawer on the first one with warnings-only navigation enabled.
  const handleOpenFirstWithWarnings = useCallback(() => {
    const filtersActive =
      !!statusFilter || !!search || !isNil(activeTemplateFilter)
    if (filtersActive) {
      setStatusFilter(undefined)
      setTemplateFilter(undefined)
      setSearch('')
      setDebouncedSearch('')
      setPendingOpenFirstWarning(true)
      return
    }
    const target = deliveries.find(hasUnresolvedWarnings)
    if (isNil(target)) {
      notifyNoWarningsVisible()
      return
    }
    setWarningsOnlyNav(true)
    setDrawerDelivery(target)
  }, [deliveries, statusFilter, search, activeTemplateFilter])

  // No manual refetch needed — useMatchDeliveryMutation invalidates both
  // QUERY_KEY (deliveries) and STATS_QUERY_KEY on success, which the
  // page-level queries are subscribed to and refetch automatically.
  const handleCloseResolveModal = useCallback(() => {
    setResolveModalOpen(false)
  }, [])

  const isBannerVisible = stats.unmatched > 0 && !bannerDismissed
  const isWarningBannerVisible =
    stats.withWarnings > 0 && !warningBannerDismissed

  return {
    billingMonth,
    billingYear,
    billingPeriodStart,
    billingPeriodLabel: selectedMonth.format('MMMM YYYY'),
    handleBillingMonthChange,
    handleBillingYearChange,

    deliveries,
    monthDeliveries,
    totalDeliveries,
    getTemplateRowInfo,
    isLoading: deliveriesQuery.isLoading,
    isError: deliveriesQuery.isError,
    stats,

    search,
    setSearch,
    statusFilter,
    handleStatusFilter,
    templateFilter: activeTemplateFilter,
    setTemplateFilter,
    allowedTemplates,
    isBannerVisible,
    handleDismissBanner,
    isWarningBannerVisible,
    handleDismissWarningBanner,
    showArchived,
    handleToggleArchived,

    resolveModalOpen,
    handleOpenResolveModal,
    handleOpenFirstWithWarnings,
    handleCloseResolveModal,

    reviewModal,
    handleOpenReview,
    handleCloseReview,
    handleReviewSent,

    isMutating,
    sendingIds,
    isBulkSending,
    isBulkSyncing,
    handleSend,
    handleBulkSync,
    handleBulkSend,
    handleUploadComplete,

    isDownloadingArchive,
    downloadedBytesLabel,
    handleDownloadSelected,
    handleDownloadMonth,

    handleDelete,
    handleSetArchived,

    selectedRowKeys: effectiveSelectedRowKeys,
    selectedSendableCount: selectedSendableIds.length,
    handleSelectedRowKeysChange,

    drawerDelivery,
    currentDeliveryIndex: currentNavIndex,
    totalInDrawerList: navDeliveries.length,
    canNavigatePrev,
    canNavigateNext,
    handleRowClick,
    handleDrawerClose,
    handleDrawerNavigate,
  }
}

export default useInvoiceDelivery
